import * as React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input, Alert, Typography, theme } from 'antd';
import { sendPasswordResetEmail } from '../../../auth/passwordReset.service';

const { Text, Paragraph } = Typography;
const { useToken } = theme;

function ForgotPassword({ open, handleClose }) {
  const { token } = useToken();
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState('');
  const [emailError, setEmailError] = React.useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();

    // Reset states
    setError('');
    setEmailError(false);

    // Validate email
    if (!email || !validateEmail(email)) {
      setEmailError(true);
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setLoading(true);
      await sendPasswordResetEmail(email);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    setEmailError(false);
    setLoading(false);
  };

  const handleCloseDialog = () => {
    handleReset();
    handleClose();
  };

  const footer = success ? (
    <Button type="primary" block onClick={handleCloseDialog}>
      Close
    </Button>
  ) : (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: token.marginXS }}>
      <Button onClick={handleCloseDialog} disabled={loading}>
        Cancel
      </Button>
      <Button
        type="primary"
        onClick={handleSubmit}
        loading={loading}
        disabled={!email.trim() && !loading}
        style={{ minWidth: 140, fontWeight: token.fontWeightStrong }}
      >
        Send Reset Link
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={handleCloseDialog}
      title="Reset Password"
      footer={footer}
      width={576}
    >
      {success ? (
        <div style={{ textAlign: 'center', padding: `${token.paddingSM}px 0` }}>
          <Alert
            type="info"
            message={<strong>Request received</strong>}
            style={{ marginBottom: token.margin }}
          />
          <Paragraph style={{ marginBottom: token.margin }}>
            If <strong>{email}</strong> belongs to an account that has already
            completed password setup, a reset link will be sent to that inbox.
          </Paragraph>
          <Text type="secondary" style={{ display: 'block', lineHeight: token.lineHeight }}>
            If this is your first time setting a password, please use the setup
            email sent by your admin instead.
          </Text>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <Paragraph style={{ marginBottom: token.marginLG }}>
            Enter the email for an account that has already set a password.
            First-time setup must be completed from the admin invitation email.
          </Paragraph>

          {error && (
            <Alert
              type="error"
              message={error}
              style={{ marginBottom: token.margin }}
            />
          )}

          <div>
            <Text>Email Address</Text>
            <Input
              autoFocus
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              status={emailError ? 'error' : ''}
              disabled={loading}
              style={{ marginTop: token.marginXXS }}
            />
            {emailError && (
              <Text type="danger" style={{ fontSize: token.fontSizeSM }}>
                Please enter a valid email address
              </Text>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
}

ForgotPassword.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default ForgotPassword;
